import { Component, inject, signal, Signal, WritableSignal, ChangeDetectionStrategy } from "@angular/core";
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from "@openng/optimus-ui/dynamicdialog";
import { InputTextModule } from "@openng/optimus-ui/inputtext";
import { SelectModule } from "@openng/optimus-ui/select";
import { first, map, Observable } from "rxjs";
import { toSignal } from "@angular/core/rxjs-interop";
import { MessageService } from "@openng/optimus-ui/api";

import { ACTION_TYPE, ActionType } from "../../../../../constants/actionTypes";
import {
  Fault,
  FaultPayload,
  FaultType,
  FaultTranslationPayload,
  Translation,
} from "../../../../../models/api/fault/fault";
import { DropdownOption } from "../../../../../models/ui/dropdownOption";
import { FaultListService } from "../../../../../services/api/fault-list/fault-list.service";
import { ButtonComponent } from "../../../../../shared/components/button/button.component";
import { SvgIconComponent } from "../../../../../shared/components/svg-icon/svg-icon.component";
import { TranslationComponent } from "../translation/translation.component";

const FAULT_TYPES: FaultType[] = [
  "MAJOR",
  // "MINOR",
  "CRITICAL",
  // "INDETERMINATE",
  // "WARNING"
];
const LANGUAGE_CODE_PATTERN = /^[a-z]{2,3}$/;

interface FaultApiError {
  status?: number;
  message?: string;
  error?: {
    code?: string;
    message?: string;
    errors?: {
      field?: string;
      message?: string;
    }[];
  };
}

@Component({
  standalone: true,
  selector: "app-fault",
  imports: [ButtonComponent, ReactiveFormsModule, TranslateModule, InputTextModule, SelectModule, SvgIconComponent],
  templateUrl: "./fault.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: "./fault.component.scss",
})
export class FaultComponent {
  private config = inject(DynamicDialogConfig);
  private dialog = inject(DialogService);
  private ref = inject(DynamicDialogRef);
  private message = inject(MessageService);
  public translate = inject(TranslateService);
  private faultListService = inject(FaultListService);

  public actionType: ActionType = this.config.data?.actionType ?? ACTION_TYPE.ADD;
  public faultListId = String(this.config.data?.faultListId ?? "");
  public fault: WritableSignal<Fault> = signal({
    ...(this.config.data?.fault ?? {}),
    translations: (this.config.data?.fault?.translations ?? []).map((translation: Translation) => ({ ...translation })),
  });
  public isSaving = signal(false);
  public serverErrors = signal<Record<string, string>>({});

  public form = new FormGroup({
    faultCode: new FormControl<string>(this.fault().faultCode ?? "", {
      nonNullable: true,
      validators: [Validators.required, Validators.maxLength(100)],
    }),
    faultType: new FormControl<FaultType | null>(this.asFaultType(this.fault().faultType), Validators.required),
  });

  public languages: Signal<DropdownOption[]> = toSignal(
    this.faultListService.getFaultListLanguage().pipe(
      map((languages) =>
        languages
          .map((language) => {
            const languageCode = this.normalizeLanguageCode(language.languageCode);
            return {
              label: language.languageName ?? languageCode,
              value: languageCode,
            };
          })
          .filter((language) => language.value.length > 0)
      )
    ),
    { initialValue: [] }
  );

  public faultTypeOptions: DropdownOption[] = FAULT_TYPES.map((faultType) => ({
    label: this.translate.instant(`fault_allocation.fault_lists.faults.fault_types.${faultType}`),
    value: faultType,
  }));

  private translationRef: DynamicDialogRef<TranslationComponent> | null = null;

  public canSave(): boolean {
    return this.form.valid && !this.isSaving() && (this.fault().translations?.length ?? 0) > 0;
  }

  public canDeleteTranslation(): boolean {
    return !this.isSaving() && (this.fault().translations?.length ?? 0) > 1;
  }

  /** Opens the translation dialog with only languages that are not already in use. */
  public onTranslationAdd() {
    if (this.isSaving()) {
      return;
    }

    const availableLanguages = this.getAvailableLanguages();
    if (availableLanguages.length === 0) {
      this.message.add({
        key: "global",
        severity: "warn",
        detail: this.translate.instant("fault_allocation.fault_lists.faults.translations.max_languages_detail"),
        life: 4000,
      });
      return;
    }

    this.translationRef = this.dialog.open(TranslationComponent, {
      header: this.translate.instant("fault_allocation.fault_lists.faults.translations.add"),
      modal: true,
      contentStyle: { width: "45vw", "max-width": "800px", overflow: "auto" },
      data: {
        translation: {},
        languages: availableLanguages,
        actionType: ACTION_TYPE.ADD,
      },
      closable: true,
    });
    this.onTranslationSave();
  }

  /** Opens a translation editor. Existing translations are identified by languageCode, not by ID. */
  public onTranslationEdit(translation: Translation) {
    if (this.isSaving()) {
      return;
    }

    this.translationRef = this.dialog.open(TranslationComponent, {
      header: this.translate.instant("fault_allocation.fault_lists.faults.translations.edit"),
      modal: true,
      contentStyle: { width: "45vw", "max-width": "800px", overflow: "auto" },
      data: {
        translation,
        languages: this.getAvailableLanguages(translation),
        actionType: ACTION_TYPE.EDIT,
      },
      closable: true,
    });
    this.onTranslationSave();
  }

  /**
   * Removes a translation from the local editor draft. The complete draft is
   * persisted only when the fault is saved with PUT.
   */
  public onTranslationDelete(index: number) {
    if (!this.canDeleteTranslation()) {
      return;
    }

    const translation = this.fault().translations?.[index];
    if (!translation) {
      return;
    }

    const languageCode = this.normalizeLanguageCode(translation.languageCode);
    this.removeTranslationByLanguageCode(languageCode);
  }

  private removeTranslationByLanguageCode(languageCode: string): void {
    this.fault.update((fault) => ({
      ...fault,
      translations: (fault.translations ?? []).filter(
        (translation) => this.normalizeLanguageCode(translation.languageCode) !== languageCode
      ),
    }));
  }

  /** Creates or updates exactly one fault and only closes after the server accepts it. */
  public save() {
    this.serverErrors.set({});
    this.form.markAllAsTouched();

    if (!this.form.valid) {
      return;
    }

    const payload = this.toPayload();
    if (!this.validatePayload(payload)) {
      return;
    }

    const request$ = this.getMutationRequest(payload);
    if (!request$) {
      return;
    }

    this.isSaving.set(true);
    request$.pipe(first()).subscribe({
      next: (serverFault) => {
        this.isSaving.set(false);
        this.ref.close(serverFault);
      },
      error: (error) => {
        this.isSaving.set(false);
        this.handleMutationError(error);
      },
    });
  }

  public closeDialog() {
    this.ref.close();
  }

  public getTranslationError(index: number): string | undefined {
    const errors = this.serverErrors();
    const prefix = `translations.${index}.`;
    return Object.entries(errors).find(([field]) => field.startsWith(prefix))?.[1];
  }

  private getMutationRequest(payload: FaultPayload): Observable<Fault> | null {
    if (this.actionType === ACTION_TYPE.ADD) {
      if (!this.faultListId) {
        this.serverErrors.set({
          _global: this.translate.instant("fault_allocation.fault_lists.faults.missing_fault_list"),
        });
        return null;
      }

      return this.faultListService.createFault(this.faultListId, payload);
    }

    const faultId = this.fault().id;
    if (!faultId) {
      this.serverErrors.set({ _global: this.translate.instant("fault_allocation.fault_lists.faults.missing_fault") });
      return null;
    }

    return this.faultListService.updateFault(String(faultId), payload);
  }

  private toPayload(): FaultPayload {
    const values = this.form.getRawValue();
    const translations = (this.fault().translations ?? []).map((translation) => this.toTranslationPayload(translation));

    return {
      faultCode: values.faultCode.trim(),
      faultType: values.faultType as FaultType,
      translations,
    };
  }

  private toTranslationPayload(translation: Translation): FaultTranslationPayload {
    const payload: FaultTranslationPayload = {
      languageCode: this.normalizeLanguageCode(translation.languageCode),
      faultText: (translation.faultText ?? "").trim(),
      fixLinkLite: this.toNullableLink(translation.fixLinkLite),
      fixLinkExpert: this.toNullableLink(translation.fixLinkExpert),
    };

    const faultCode = translation.faultCode?.trim();
    if (faultCode) {
      payload.faultCode = faultCode;
    } else if (translation.faultCode === null) {
      payload.faultCode = null;
    }

    return payload;
  }

  private validatePayload(payload: FaultPayload): boolean {
    const errors: Record<string, string> = {};
    const languageCodes = new Set<string>();

    if (payload.translations.length === 0) {
      errors["translations"] = this.translate.instant("fault_allocation.fault_lists.faults.translations.required");
    }

    payload.translations.forEach((translation, index) => {
      const fieldPrefix = `translations.${index}`;
      if (!LANGUAGE_CODE_PATTERN.test(translation.languageCode)) {
        errors[`${fieldPrefix}.languageCode`] = this.translate.instant(
          "fault_allocation.fault_lists.faults.translations.invalid_language"
        );
      } else if (languageCodes.has(translation.languageCode)) {
        errors[`${fieldPrefix}.languageCode`] = this.translate.instant(
          "fault_allocation.fault_lists.faults.translations.duplicate_language"
        );
      }
      languageCodes.add(translation.languageCode);

      if (!translation.faultText) {
        errors[`${fieldPrefix}.faultText`] = this.translate.instant(
          "fault_allocation.fault_lists.faults.translations.required_fault_text"
        );
      } else if (translation.faultText.length > 1000) {
        errors[`${fieldPrefix}.faultText`] = this.translate.instant(
          "fault_allocation.fault_lists.faults.translations.fault_text_too_long"
        );
      }
    });

    const faultColumnTranslation =
      payload.translations.find((translation) => translation.languageCode === "de") ?? payload.translations[0];
    if (faultColumnTranslation?.faultText.length > 300) {
      const faultColumnTranslationIndex = payload.translations.indexOf(faultColumnTranslation);
      errors[`translations.${faultColumnTranslationIndex}.faultText`] = this.translate.instant(
        "fault_allocation.fault_lists.faults.translations.fault_column_text_too_long"
      );
    }

    this.serverErrors.set(errors);
    return Object.keys(errors).length === 0;
  }

  private handleMutationError(error: unknown) {
    const apiError = error as FaultApiError;
    const fieldErrors = this.getFieldErrors(apiError);
    const detail = apiError.error?.message || apiError.message || this.getStatusMessage(apiError.status);
    this.addFaultCodeNotEditableError(apiError, fieldErrors, detail);
    if (Object.keys(fieldErrors).length === 0) {
      fieldErrors["_global"] = detail;
    }
    this.serverErrors.set(fieldErrors);

    this.message.add({
      key: "global",
      severity: "error",
      summary: this.translate.instant("common.server_error"),
      detail,
      life: 4000,
    });
  }

  private getFieldErrors(apiError: FaultApiError): Record<string, string> {
    const fieldErrors: Record<string, string> = {};

    for (const fieldError of apiError.error?.errors ?? []) {
      if (!fieldError.field || !fieldError.message) {
        continue;
      }

      fieldErrors[fieldError.field] = fieldError.message;
      if (fieldError.field === "faultCode" || fieldError.field === "faultType") {
        const control = this.form.controls[fieldError.field];
        control.setErrors({ ...(control.errors ?? {}), server: fieldError.message });
      }
    }

    return fieldErrors;
  }

  private addFaultCodeNotEditableError(
    apiError: FaultApiError,
    fieldErrors: Record<string, string>,
    detail: string
  ): void {
    if (apiError.error?.code !== "FAULT_CODE_NOT_EDITABLE") {
      return;
    }

    fieldErrors["faultCode"] = detail;
    this.form.controls.faultCode.setErrors({ ...(this.form.controls.faultCode.errors ?? {}), server: detail });
  }

  private getStatusMessage(status: number | undefined): string {
    switch (status) {
      case 401:
        return this.translate.instant("error_messages.UNAUTHORIZED");
      case 403:
        return this.translate.instant("error_messages.FORBIDDEN");
      case 404:
        return this.translate.instant("error_messages.NOT_FOUND", {
          object: this.translate.instant("common.entities.fault_list"),
        });
      default:
        return this.translate.instant("error_messages.BAD_REQUEST");
    }
  }

  private onTranslationSave() {
    this.translationRef?.onClose.subscribe({
      next: (newTranslation: Translation | null) => {
        if (!newTranslation) {
          return;
        }

        const normalizedTranslation = this.normalizeTranslation(newTranslation);
        this.fault.update((fault) => {
          const translations = fault.translations ?? [];
          const existingIndex = translations.findIndex(
            (translation) => this.normalizeLanguageCode(translation.languageCode) === normalizedTranslation.languageCode
          );

          return {
            ...fault,
            translations:
              existingIndex === -1
                ? [...translations, normalizedTranslation]
                : translations.map((translation, index) =>
                    index === existingIndex ? normalizedTranslation : translation
                  ),
          };
        });
      },
    });
  }

  private getAvailableLanguages(translation?: Translation): DropdownOption[] {
    const currentlyEditedLanguage = this.normalizeLanguageCode(translation?.languageCode);
    const usedLanguages = new Set(
      (this.fault().translations ?? [])
        .map((faultTranslation) => this.normalizeLanguageCode(faultTranslation.languageCode))
        .filter(Boolean)
    );

    return this.languages().filter((language) => {
      const languageCode = this.normalizeLanguageCode(String(language.value ?? ""));
      return languageCode.length > 0 && (!usedLanguages.has(languageCode) || languageCode === currentlyEditedLanguage);
    });
  }

  private normalizeTranslation(translation: Translation): Translation {
    return {
      ...translation,
      languageCode: this.normalizeLanguageCode(translation.languageCode),
      faultText: (translation.faultText ?? "").trim(),
      fixLinkLite: this.toNullableLink(translation.fixLinkLite),
      fixLinkExpert: this.toNullableLink(translation.fixLinkExpert),
    };
  }

  private normalizeLanguageCode(value: string | null | undefined): string {
    return (value ?? "").trim().toLowerCase().split(/[-_]/, 1)[0] ?? "";
  }

  private toNullableLink(value: string | null | undefined): string | null {
    const link = value?.trim();
    return link ? link : null;
  }

  private asFaultType(value: string | null | undefined): FaultType | null {
    return value && FAULT_TYPES.includes(value as FaultType) ? (value as FaultType) : null;
  }
}
